import { differenceInCalendarDays, subDays, subYears } from 'shared/dateFns'
import { CUSTOM_PRESET_VALUE, type ComparisonPresetsProps, type DateRange } from '@precis-digital/kurama/DatePickerV2'
import { useMemo } from 'react'
import { useTranslation } from 'shared/translations'

export const DATE_PICKER_V2_COMPARISON_PRESET_VALUES = ['previousPeriod', 'previousYearsPeriod'] as const

export type DatePickerV2ComparisonPresetValue = (typeof DATE_PICKER_V2_COMPARISON_PRESET_VALUES)[number]

const COMPARISON_PRESET_VALUES: ReadonlySet<string> = new Set(DATE_PICKER_V2_COMPARISON_PRESET_VALUES)

export const isDatePickerV2ComparisonPresetValue = (value: string): value is DatePickerV2ComparisonPresetValue =>
  COMPARISON_PRESET_VALUES.has(value)

export const getDateFromDatePickerV2ComparisonPreset = (
  selectedDate: Date,
  presetValue: DatePickerV2ComparisonPresetValue
): Date => (presetValue === 'previousPeriod' ? subDays(selectedDate, 1) : subYears(selectedDate, 1))

export const getDateRangeFromDatePickerV2ComparisonPreset = (
  selectedRange: DateRange,
  presetValue: DatePickerV2ComparisonPresetValue
): DateRange => {
  if (presetValue === 'previousYearsPeriod') {
    return {
      from: subYears(selectedRange.from, 1),
      to: selectedRange.to == null ? undefined : subYears(selectedRange.to, 1)
    }
  }

  const end = selectedRange.to ?? selectedRange.from
  const days = differenceInCalendarDays(end, selectedRange.from)
  const to = subDays(selectedRange.from, 1)
  return { from: subDays(to, days), to }
}

const resolveComparisonDate: ComparisonPresetsProps['getComparisonDateFromPreset'] = (selectedDate, presetValue) =>
  isDatePickerV2ComparisonPresetValue(presetValue)
    ? getDateFromDatePickerV2ComparisonPreset(selectedDate, presetValue)
    : null

const resolveComparisonRange: ComparisonPresetsProps['getComparisonRangeFromPreset'] = (selectedRange, presetValue) =>
  isDatePickerV2ComparisonPresetValue(presetValue)
    ? getDateRangeFromDatePickerV2ComparisonPreset(selectedRange, presetValue)
    : null

export const useDatePickerV2ComparisonPresets = (): ComparisonPresetsProps => {
  const { t } = useTranslation('common')

  return useMemo(
    () => ({
      presets: [
        { label: t('datePickerV2.comparisonPresets.previousPeriod'), value: 'previousPeriod' },
        { label: t('datePickerV2.comparisonPresets.previousYearsPeriod'), value: 'previousYearsPeriod' },
        { label: t('datePickerV2.presets.custom'), value: CUSTOM_PRESET_VALUE }
      ],
      getComparisonDateFromPreset: resolveComparisonDate,
      getComparisonRangeFromPreset: resolveComparisonRange
    }),
    [t]
  )
}
